import type { FunctionComponent } from 'react';

import type {
  BlocklistConfidence,
  BlocklistMatch,
  BlocklistMatchType,
} from '@ballerine/business-analysis-schemas';

import { ctw } from '@/common';

const MATCH_TYPE_LABELS: Record<BlocklistMatchType, string> = {
  url: 'URL',
  company_name: 'Company Name',
  registration_number: 'Registration Number',
  address: 'Address',
};

// Exact Figma pill tones (node 13327:6284/6287) — the shared severity map reads too washed-out here.
const CONFIDENCE_PILL_CLASSES: Record<BlocklistConfidence, string> = {
  high: 'bg-[#df2222]/20 text-[#df2222]',
  medium: 'bg-[#ffb35a]/10 text-[#e7922e]',
};

const toExternalHref = (value: string) => (value.startsWith('http') ? value : `https://${value}`);

type BlocklistTableProps = {
  matches: BlocklistMatch[];
};

export const BlocklistTable: FunctionComponent<BlocklistTableProps> = ({ matches }) => (
  <table className="w-full border-collapse text-sm">
    <thead>
      <tr className="border-b border-zinc-200 text-left font-medium text-[#787981]">
        <th className="px-4 py-2 font-medium">Match</th>
        <th className="px-4 py-2 font-medium">Matched Property</th>
        <th className="px-4 py-2 font-medium">Property</th>
        <th className="px-4 py-2 font-medium">Blocking Reason</th>
        <th className="px-4 py-2 font-medium">Confidence</th>
      </tr>
    </thead>
    <tbody>
      {matches.map((match, index) => (
        <tr key={`${match.matchType}-${match.matchedValue}-${index}`} className="even:bg-slate-50">
          <td className="px-4 py-4 font-semibold text-black">{index + 1}</td>
          <td className="px-4 py-4 text-black">
            {MATCH_TYPE_LABELS[match.matchType] ?? match.matchType}
          </td>
          <td className="max-w-[16rem] truncate px-4 py-4 text-black">
            {match.matchType === 'url' ? (
              <a
                href={toExternalHref(match.matchedValue)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#007aff] hover:underline"
              >
                {match.matchedValue}
              </a>
            ) : (
              match.matchedValue
            )}
          </td>
          <td className="px-4 py-4 text-black">{match.blockingReason ?? '—'}</td>
          <td className="px-4 py-4">
            <span
              className={ctw(
                'inline-block rounded px-1 text-xs capitalize',
                CONFIDENCE_PILL_CLASSES[match.confidence],
              )}
            >
              {match.confidence}
            </span>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);
